import React, { useState, useEffect, useContext } from "react";
import { SurveyContext } from "../../context/SurveyContext";
import api from "../../services/api";
import { getProfilePicture } from "../../utils/avatar";
import { FaRegComment, FaRetweet, FaRegHeart } from "react-icons/fa";

/**
 * Displays 2-3 post+comment threads from the participant's instance
 * as a memory refresher on survey pages.
 *
 * Prioritizes threads the participant engaged in (commented/liked).
 * Shows only agent content — no participant posts (to avoid self-consistency bias).
 * Rendered in read-only platform style.
 */
const DiscussionReminder = () => {
  const { instanceId } = useContext(SurveyContext);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!instanceId) { setLoading(false); return; }

    const fetchThreads = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { data } = await api.get("/messages/reminder-threads", {
          params: { instanceId, limit: 3 },
          headers,
        });
        setThreads(data || []);
      } catch (e) {
        console.warn("Failed to load discussion reminder:", e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchThreads();
  }, [instanceId]);

  if (loading || threads.length === 0) return null;

  return (
    <div className="mb-6">
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="flex items-center gap-2 text-left w-full group"
      >
        <div className="w-1 h-4 bg-spiders-primary-500 rounded-full flex-shrink-0" />
        <p className="text-xs font-medium text-spiders-primary-400 group-hover:text-spiders-primary-300 transition-colors">
          {expanded
            ? "Hide discussion examples"
            : "Need a reminder of the discussion? Click here to see example threads"}
        </p>
        <svg
          className={`w-3.5 h-3.5 text-spiders-primary-400 transition-transform flex-shrink-0 ${expanded ? "rotate-180" : ""}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="space-y-3 mt-3">
          {threads.map((thread, idx) => (
            <ThreadCard key={idx} thread={thread} />
          ))}
        </div>
      )}
    </div>
  );
};

const ThreadCard = ({ thread }) => {
  const { post, comments } = thread;
  if (!post) return null;

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    const now = new Date();
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 60) return `${diffMin}m`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `${diffH}h`;
    return `${Math.round(diffH / 24)}d`;
  };

  return (
    <div className="rounded-xl border border-spiders-border bg-spiders-bg-surface overflow-hidden">
      {/* Post */}
      <div className="p-4">
        <div className="flex space-x-3">
          <img
            src={getProfilePicture(post.author?.username || "unknown", post.author?.avatarSeed)}
            alt=""
            className="w-10 h-10 rounded-full flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-x-1.5 mb-1">
              <span className="font-bold text-spiders-text-primary text-sm">
                @{post.author?.username || "unknown"}
              </span>
              <span className="text-spiders-text-muted text-xs">
                {formatTime(post.timestamp)}
              </span>
            </div>
            <div className="text-spiders-text-primary text-[15px] leading-normal whitespace-pre-wrap">
              {post.content}
            </div>
            <div className="flex items-center mt-2 gap-4 text-spiders-text-muted text-xs">
              <span className="flex items-center gap-1">
                <FaRegComment className="w-3.5 h-3.5" />
                {post.commentCount || comments?.length || 0}
              </span>
              <span className="flex items-center gap-1">
                <FaRegHeart className="w-3.5 h-3.5" />
                {post.likeCount || 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Comments */}
      {comments && comments.length > 0 && (
        <div className="border-t border-spiders-border">
          {comments.map((comment, cIdx) => (
            <div key={cIdx} className="ml-8 mr-4 py-3 flex items-start gap-2.5 border-l-2 border-spiders-primary-500/30 pl-4">
              <img
                src={getProfilePicture(comment.author?.username || "unknown", comment.author?.avatarSeed)}
                alt=""
                className="w-7 h-7 rounded-full flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 mb-0.5">
                  <span className="font-semibold text-spiders-text-secondary text-xs">
                    @{comment.author?.username || "unknown"}
                  </span>
                  <span className="text-spiders-text-muted text-xs">
                    {formatTime(comment.timestamp)}
                  </span>
                </div>
                <p className="text-spiders-text-secondary text-xs leading-relaxed">
                  {comment.content}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DiscussionReminder;
